import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaBook, FaTrash, FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getStudyMaterials,
  clearState,
  deleteSubjects,
} from "../redux/Slices/StudyMaterialsSlice";
import { useTitle } from "../context/TitleContext";
import PermissionButton from "../components/PermissionButton";
import Pagination from '../components/Pagination';

const StudyMaterials = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingMaterial, setDeletingMaterial] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    studyMaterials,
    meta,
    loading,
    error,
    deleteSuccessMessage,
  } = useSelector((state) => state.studyMaterials);

  useEffect(() => {
    setTitle("المواد");
  }, [setTitle]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(getStudyMaterials({ page: currentPage, per_page: 10, search: searchQuery }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (deleteSuccessMessage) {
      setShowDeleteModal(false);
      setDeletingMaterial(null);
      setTimeout(() => {
        toast.success("تم الحذف بنجاح", {
          position: "top-center",
          zIndex: 9999,
        });
      }, 100);
      dispatch(getStudyMaterials({ page: currentPage, per_page: 10, search: searchQuery }));
      dispatch(clearState());
    }
    if (error) {
      toast.error("لم يتم الحذف", {
        position: "top-center",
        zIndex: 9999,
      });
      dispatch(clearState());
    }
  }, [deleteSuccessMessage, error, dispatch, currentPage, searchQuery]);

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(getStudyMaterials({ page: 1, per_page: 10, search: searchQuery }));
  };

  const handleDelete = (material) => {
    setDeletingMaterial(material);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingMaterial) {
      dispatch(deleteSubjects(deletingMaterial.id));
      setShowDeleteModal(false);
      setDeletingMaterial(null);
    }
  };



  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (loading && (!studyMaterials || studyMaterials.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .sub-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .sub-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .sub-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            📚 المواد الدراسية
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>إدارة وتعديل المواد التعليمية المضافة للموقع</p>
        </div>
        <PermissionButton
          permission="create-subjects"
          onClick={() => navigate("/add-study-material")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          <FaPlus size={12} /> إضافة مادة جديدة
        </PermissionButton>
      </div>

      {/* ── Search Bar ───────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 22px", marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 8, textAlign: "right" }}>البحث السريع في المواد</label>
        <form className="d-flex align-items-center" onSubmit={handleSearch} style={{ maxWidth: 500, gap: 10 }}>
          <input
            style={{
              width: "100%",
              padding: "9px 14px",
              borderRadius: 9,
              border: "1.5px solid #e2e8f0",
              fontSize: 14,
              outline: "none",
              boxSizing: "border-box",
              backgroundColor: "#fff",
              transition: "border-color 0.2s, box-shadow 0.2s"
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="ابحث عن مادة..."
            onFocus={(e) => {
              e.target.style.borderColor = "var(--main-color)";
              e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
          <button className="btn btn-primary btn-sm" type="submit" style={{ padding: "10px 18px", borderRadius: 9 }}><i className="bi bi-search"></i></button>
          {searchQuery && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setSearchQuery("");
                setCurrentPage(1);
                dispatch(getStudyMaterials({ page: 1, per_page: 10, search: "" }));
              }}
              style={{ padding: "9px 16px", borderRadius: 9, minWidth: "110px" }}
            >
              مسح البحث
            </button>
          )}
        </form>
      </div>

      {/* ── Table / Cards Container ─────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!studyMaterials || studyMaterials.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <FaBook size={48} className="text-muted mb-3" />
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد مواد دراسية مسجلة</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="sub-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>اسم المادة (عربي)</th>
                    <th>اسم المادة (إنجليزي)</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {studyMaterials.map((material, index) => (
                    <tr key={material.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>
                        {material.name_ar}
                      </td>
                      <td style={{ color: "#475569" }}>
                        {material.name_en}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: material.status === "active" ? "#15803d" : "#64748b", background: material.status === "active" ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {material.status === "active" ? "مفعل" : "معطل"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => {
                              sessionStorage.setItem(
                                `studyMaterial_${material.id}`,
                                JSON.stringify({
                                  id: material.id,
                                  name_ar: material.name_ar,
                                  status: material.status,
                                })
                              );
                              navigate(`/edit-study-material/${material.id}`);
                            }}
                            permission="edit-subjects"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(material)}
                            permission="delete-subjects"
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* ── Delete Modal ──────────────────────────────── */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingMaterial(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذه المادة الدراسية؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                📚 {deletingMaterial?.name_ar}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingMaterial(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={loading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {loading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف المادة"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyMaterials;
